<template>
  <div>
    <div style="margin-bottom: 5px">
      {{ props.header }}
    </div>
    <div class="input-container">
      <input v-if="props.type === 'text' || props.type === 'password'"
             :value="props.text"
             :type="props.type"
             class="text-input"
             :name="props.name"
             :style="props.style"
             :readonly="props.isReadonly"
             @input="setValue"
             @focus="handleFocus"
             @blur="handleBlur"
      >
      <textarea v-else-if="props.type === 'textarea'"
                :value="props.text"
                class="text-input"
                :name="props.name"
                :style="props.style"
                :readonly="props.isReadonly"
                @input="setValue"
                @focus="handleFocus"
                @blur="handleBlur"
      />

      <select v-else-if="props.type === 'select'"
              :value="props.selectedItem"
              :name="props.name"
              :style="props.style"
              :readonly="props.isReadonly"
              :class="{ 'focused': isFocused || props.selectedItem }"
              @change="selectItem"
              @focus="handleFocus"
              @blur="handleBlur"
      >
        <!-- Handle key-value pairs -->
        <template v-if="isKeyValueOptions(props.options)">
          <option v-for="(label, key) in props.options"
                  :key="key"
                  :value="key"
          >
            {{ label }}
          </option>
        </template>

        <!-- Handle simple values -->
        <template v-if="isSimpleValues(props.options)">
          <option v-for="(option, index) in props.options"
                  :key="index"
                  :value="option"
          >
            {{ option }}
          </option>
        </template>

        <!-- Handle object options -->
        <template v-if="isObjectValues(props.options)">
          <option v-for="(option, index) in props.options"
                  :key="option.Id || index"
                  :value="option.Id || option"
          >
            {{ typeof option === 'object' ? `$${option.Name}` : option }}
          </option>
        </template>
      </select>
      <div v-else-if="props.type === 'selectSearch'"
           style="position: relative;"
      >
        <input ref="inputField"
               :value="inputValue"
               class="text-input"
               :name="props.name"
               :style="props.style"
               :readonly="props.isReadonly"
               @input="handleInput"
               @focus="showList"
               @focusout="handleFocusOut"
               @blur="hideList"
        >
        <ul v-if="isListVisible"
            class="options-list"
            :style="props.style"
        >
          <template v-if="isObjectValues(filteredOptions)">
            <li v-for="(option, index) in filteredOptions"
                :key="option.Id || index"
                class="option-item"
                @mousedown.prevent="selectOption(option)"
            >
              {{ option.Name }}
            </li>
          </template>
        </ul>
      </div>
      <span class="floating-label"
            :class="{ 'active': isFocused || props.text || userInput || (props.selectedItem && typeof props.selectedItem === 'string') }"
      >
        {{ props.placeholder }}
      </span>
    </div>
  </div>
</template>

<script setup>
import { defineProps, defineEmits, ref, computed, watch } from 'vue'

const emit = defineEmits(['valueChanged'])

const props = defineProps({
  text: {
    type: String,
    default: ''
  },
  selectedItem: {
    type: [String, Object],
    default: ''
  },
  options: {
    type: [Object, Array],
    default: () => ({})
  },
  type: {
    type: String,
    default: 'text'
  },
  placeholder: {
    type: String,
    default: ''
  },
  name: {
    type: String,
    default: ''
  },
  isReadonly: {
    type: Boolean,
    default: false
  },
  style: {
    type: String,
    default: ''
  },
  header: {
    type: String,
    default: ''
  },
  clearSearch: {
    type: Boolean,
    default: false
  }
})

const isFocused = ref(false)
const isListVisible = ref(false)
const userInput = ref(props.text)
const selectedObject = ref(null)
const isTyping = ref(false) // Track if user is typing

watch(() => props.clearSearch, (newVal) => {
  if (newVal) {
    userInput.value = '' // Clear the search query when clearSearch is true
    selectedObject.value = null
  }
})
function handleFocus () {
  isFocused.value = true
}
function handleFocusOut () {
  isFocused.value = false
}
function handleBlur () {
  if (props.type === 'select' && !props.selectedItem) {
    isFocused.value = false
  } else if (props.type !== 'select') {
    isFocused.value = false
  }
}

function handleInput (e) {
  userInput.value = e.target.value
  isTyping.value = true // Set typing flag when user types
  selectedObject.value = null // Clear the selected object when typing
  emit('valueChanged', null)
  showList()
}

function showList () {
  isListVisible.value = true
  isFocused.value = true
}

function setValue (e) {
  emit('valueChanged', e)
}

function hideList () {
  setTimeout(() => (isListVisible.value = false), 1) // Timeout prevents the list from closing immediately when clicking an option
}

function selectOption (option) {
  selectedObject.value = option
  userInput.value = `${option.Name}`
  isTyping.value = false // Reset typing flag after selecting an option
  emit('valueChanged', option)
  isListVisible.value = false
}

const isObjectValues = (options) => {
  return Array.isArray(options) && typeof options[0] === 'object'
}

function selectItem (e) {
  const selectedItem = e.target.value
  emit('valueChanged', selectedItem)
}

const filteredOptions = computed(() => {
  const search = userInput.value ? userInput.value.toLowerCase() : ''

  if (isObjectValues(props.options)) {
    return props.options.filter(option => {
      const nameMatch = option.Name && option.Name.toLowerCase().includes(search)
      return nameMatch // Return true if any of them matches
    })
  }

  return []
})

const inputValue = computed(() => {
  if (isTyping.value) {
    return userInput.value
  }
  if (selectedObject.value) {
    return `${selectedObject.value.Name}`
  }
  return userInput.value
})
const isKeyValueOptions = (options) => {
  return typeof options === 'object' && !Array.isArray(options)
}

const isSimpleValues = (options) => {
  return Array.isArray(options) && typeof options[0] === 'string'
}

</script>

<style lang="scss" scoped>

select {
  width: 100%;
  border: 0;
  padding: 15px;
  padding-top: 22px;
  margin-bottom: 15px;
  outline: 0;
  font-size: 15px;
  text-align: center;
  box-sizing: border-box;
  background: whitesmoke;
  border-radius: 20px 5px;
  transition: 0.2s ease-out;
  color: var(--dark);

  &:focus {
    transition: 0.2s ease-out;
    border-radius: 5px 20px;
    outline: 2px solid var(--altprimary);
  }

  &:read-only {
    color: rgb(73, 73, 73);
  }
}

.select-container {
  position: relative;
  width: 100%;
}

.dropdown-wrapper {
  position: relative;
}

.search-box-wrapper {
  position: relative;
  margin-bottom: 5px;
}

.search-box {
  width: 100%;
  padding: 18px 10px 8px;
  font-size: 14px;
  border: 1px solid #ccc;
  border-radius: 5px;
  box-sizing: border-box;
  height: 50px;
  line-height: 30px;
}

.floating-label-search {
  position: absolute;
  left: 12px;
  top: 15px;
  font-size: 14px;
  color: #999;
  pointer-events: none;
  transition: 0.2s ease all;
}

.floating-label-search.active {
  top: 5px;
  font-size: 12px;
  font-weight: bold;
  color: var(--mainBlue);
}

.dropdown-options {
  position: absolute;
  font-size: 16px; /* Shrink font size when active */
  top: 100%;
  left: 0;
  width: 100%;
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid #ccc;
  background-color: white;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
  z-index: 10;
  border-radius: 0 0 5px 5px;
}

.dropdown-item {
  padding: 10px;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.dropdown-item:hover {
  background-color: #f1f1f1;
}

.dropdown-item:active {
  background-color: #e1e1e1;
}

.input-container {
  position: relative;
}

.options-list {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  max-height: 150px;
  overflow-y: auto;
  border: 1px solid #ccc;
  z-index: 9999;
}

.text-input {
  width: 100%;
  border: 0;
  padding-bottom: 15px;
  padding-left: 15px;
  padding-right: 15px;
  margin-bottom: 15px;
  outline: 0;
  font-size: 15px;
  text-align: left;
  box-sizing: border-box;
  background: whitesmoke;
  border-radius: 5px 15px;
  transition: 0.2s ease-out;
  resize: none;
  border-top: 24px solid transparent;
  border-right: 0px solid transparent;
  border-left: 0px solid transparent;

  &:focus {
    transition: 0.2s ease-out;
    border-radius: 15px 5px;
    outline: 2px solid var(--altprimary);
  }

  ::placeholder {
    color: #90A4AE;
    transition: 0.2s ease-out;
    font-size: 15px;
  }

  &:read-only {
    color: rgb(73, 73, 73);
  }

  &.input-error {
    border: 1px solid red;
  }
}

.floating-label {
  position: absolute;
  pointer-events: none;
  left: 20px;
  top: 40%;
  color: #64748b;
  transform: translateY(-50%);
  transition: 0.2s ease all;
  opacity: 0.5;

  &.active {
    top: 10px;
    left: 14px;
    font-size: 11px;
    font-weight: 600;
    color: var(--mainBlue);
    opacity: 1;
  }
}

.option-item {
  padding: 10px;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.option-item:hover {
  background-color: #f1f1f1;
}

.option-item:active {
  background-color: #e1e1e1;
}
</style>
